import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  ArrowLeft,
  Loader2,
  Download,
  Users,
  Flame,
  RefreshCw,
  LogOut,
  Printer,
  Copy,
  Check,
  Trash2,
  Pencil,
  X,
  PackageCheck,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import DeliveredBatchesPanel from "@/components/DeliveredBatchesPanel";

// IST = Asia/Kolkata (UTC+5:30) — used for all date/time displays
const IST_OPTS = { timeZone: "Asia/Kolkata" };
const istYesterday = () => new Date(Date.now() - 86400000).toLocaleDateString("en-CA", IST_OPTS);

function formatISTDate(yyyymmdd) {
  // input "2026-06-01" — already in IST from backend. Render as "01 Jun 2026".
  try {
    const [y, m, d] = yyyymmdd.split("-");
    const dt = new Date(Date.UTC(+y, +m - 1, +d));
    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...IST_OPTS,
    });
  } catch {
    return yyyymmdd;
  }
}

export default function LPGReport() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(1);
  // Custom date range — used when `days === "custom"`. YYYY-MM-DD (IST).
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Team-member filter — restricts aggregation to entries whose
  // `uploaded_by` matches. Empty string = All team members.
  const [staffFilter, setStaffFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("__all__");
  // Owned groups (independent of the days window) — used to detect single-
  // agency distributors even when the current window has zero deliveries.
  const [ownedGroups, setOwnedGroups] = useState([]);
  // "Delete Today" bulk-cleanup (Feb 2026 admin ask) — HARD delete of the
  // entire IST-today window, optionally scoped to one operator. Rows are
  // permanently removed from Mongo — ledger recomputes from scratch.
  const [deleteTodayOp, setDeleteTodayOp] = useState("all");
  const [deletingToday, setDeletingToday] = useState(false);
  // Per-row hard delete (Feb 2026): admin can nuke a single date × agency
  // bucket straight from the ledger table. Tracks which row is in-flight.
  const [deletingRow, setDeletingRow] = useState("");
  // Feb 2026 admin fix button: on "Today" filter only, one-click backfill
  // `status="correct"` on any entry that was committed with a null
  // status (Feb-24 regression). Also useful going forward as a self-heal.
  const [fixingLedger, setFixingLedger] = useState(false);

  // Reassign modal state (Feb 2026 admin ask): admins can move today's
  // OR any past-date dispatches from one uploader to another (also
  // agency + quantity). Buttons are HIDDEN by default and only appear
  // when the admin flips the `showReassign` toggle on the toolbar so
  // regular viewing never accidentally opens the edit UI.
  const [reassignModal, setReassignModal] = useState(null);
  const [showReassign, setShowReassign] = useState(false);

  const deleteTodayEntries = async () => {
    const opLabel =
      deleteTodayOp === "all"
        ? "ALL operators"
        : deleteTodayOp.toUpperCase();

    // SAFETY-FIRST behaviour (Feb 2026 admin ask):
    // *"delete only today wala hoga... baki pe nhi aana chahiye"*.
    // Regardless of the currently-selected date filter (Today / Last 7
    // days / Custom range), this button ALWAYS scopes to IST today only.
    // Operator + staff filters DO still apply so admins can narrow to
    // e.g. "today's Bharat dispatches by Komal" but never blow away
    // yesterday or older days by accident.
    const istToday = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    });
    const params = new URLSearchParams();
    params.set("operator", deleteTodayOp);
    params.set("from_date", istToday);
    params.set("to_date", istToday);
    if (staffFilter) params.set("staff", staffFilter);

    if (!window.confirm(
      `⚠️ SIRF AAJ (${istToday}) ki entries PERMANENTLY delete hongi\n` +
      `• Operator: ${opLabel}\n` +
      `• Team: ${staffFilter || "All members"}\n` +
      `• Purane din ki entries SAFE rahengi (touch nahi hongi).\n\n` +
      `Continue?`
    )) {
      return;
    }
    setDeletingToday(true);
    try {
      const { data } = await api.post(
        `/lpg/delete-today-entries?${params.toString()}`
      );
      toast.success(
        `Permanently deleted ${data.messages_deleted} message(s) · today only (${istToday})`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingToday(false);
    }
  };

  const deleteSingleRow = async (row) => {
    if (!row?.group_id || !row?.date) return;
    const rowKey = `${row.date}::${row.group_id}`;
    if (!window.confirm(
      `${formatISTDate(row.date)} · ${row.name || "this agency"} ki SAARI ${row.total} entries PERMANENTLY delete ho jaayengi. Sure?`
    )) {
      return;
    }
    setDeletingRow(rowKey);
    try {
      const { data } = await api.post(
        `/lpg/delete-single-entry?date=${encodeURIComponent(row.date)}&group_id=${encodeURIComponent(row.group_id)}`
      );
      toast.success(
        `Permanently deleted ${data.messages_deleted} message(s) for ${formatISTDate(row.date)} · ${row.name || row.group_id}`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingRow("");
    }
  };

  const openReassign = (row) => {
    // Build the modal state — pre-fill with first current uploader and
    // preserve current agency + current qty as the sensible default.
    const currentUploader = (row.uploaders && row.uploaders[0]) || "";
    setReassignModal({
      row,
      to_uploader: currentUploader,
      to_group_id: row.group_id, // default: same agency
      quantity: "", // admin MUST type a number ≤ row.total (mandatory)
      submitting: false,
    });
  };

  const submitReassign = async () => {
    if (!reassignModal) return;
    const { row, to_uploader, to_group_id, quantity } = reassignModal;
    const from_uploader = (row.uploaders && row.uploaders[0]) || "";
    if (!to_uploader.trim()) {
      toast.error("Select the target team member");
      return;
    }
    if (!from_uploader) {
      toast.error("Source row has no uploader tag — cannot reassign");
      return;
    }
    // Quantity is MANDATORY (Feb 2026 admin ask: never move "all"
    // silently — force admin to pick an exact number, capped at row
    // total). Blank / non-numeric / <1 / >total all reject.
    const trimmed = (quantity || "").trim();
    if (!trimmed) {
      toast.error("Quantity chahiye — kitni entries move karni hain?");
      return;
    }
    const qty = parseInt(trimmed, 10);
    if (!qty || qty < 1) {
      toast.error("Quantity kam se kam 1 honi chahiye");
      return;
    }
    if (qty > row.total) {
      toast.error(`Max quantity ${row.total} — es se jyada nahi ho sakta`);
      return;
    }
    setReassignModal((m) => ({ ...m, submitting: true }));
    try {
      const { data } = await api.post("/lpg/reassign-entries", {
        date: row.date,
        from_group_id: row.group_id,
        from_uploader,
        to_uploader: to_uploader.trim(),
        to_group_id: to_group_id || row.group_id,
        quantity: qty,
      });
      toast.success(
        `Moved ${data.moved_entries} entries → ${to_uploader}` +
          (to_group_id !== row.group_id ? " (agency changed)" : "") +
          (data.messages_created > 0 ? " · split" : "")
      );
      setReassignModal(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      setReassignModal((m) => (m ? { ...m, submitting: false } : m));
    }
  };

  const fixTodayLedger = async () => {
    // Today IST in YYYY-MM-DD (matches backend's `_today_ist`)
    const istToday = new Date().toLocaleDateString("en-CA", {
      timeZone: "Asia/Kolkata",
    });
    if (!window.confirm(
      `Aaj (${istToday}) ki jitni entries me status null hai — sab pe 'correct' set kar denge. Ledger recompute ho jaayega. Continue?`
    )) return;
    setFixingLedger(true);
    try {
      const { data } = await api.post(
        `/admin/backfill-lpg-status?date=${istToday}`
      );
      toast.success(
        `Fixed ${data.messages_modified} message(s) — Staff/Agency Ledger ab populate hoga`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFixingLedger(false);
    }
  };

  const rangeParams = () => {
    const ist = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
    if (days === "custom") return { ...(fromDate && { from_date: fromDate }), ...(toDate && { to_date: toDate }) };
    // "This month" — 1st of current IST month → today (explicit dates keep month boundaries exact).
    if (days === "month") return { from_date: ist.slice(0, 8) + "01", to_date: ist };
    if (days === "yesterday") return { from_date: istYesterday(), to_date: istYesterday() };
    // "Today" = IST calendar day, not a rolling 24h window.
    if (days === 1) return { from_date: ist, to_date: ist };
    return { days };
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const params = rangeParams();
      const istToday = days === 1 || days === "month" ? new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "";
      if (staffFilter) params.staff = staffFilter;
      // Feb 2026 self-heal: on the "Today" filter for admins, silently
      // backfill any null-status entries BEFORE reading the report.
      // This makes "Refresh" itself the fix — admin doesn't have to
      // remember to click the separate Fix Ledger button. Best-effort;
      // failures are swallowed because the report read must still
      // succeed even if the backfill is unreachable.
      if (istToday && isAdmin) {
        try {
          await api.post(
            `/admin/backfill-lpg-status?date=${istToday}`
          );
        } catch (e) {
          // Silent to the user — the manual Fix Ledger button is the
          // fallback. Log so devs can trace repeated backfill failures.
          console.error("auto-backfill-lpg-status failed (silent):", e);
        }
      }
      const { data } = await api.get(`/lpg/report`, { params });
      setData(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [days, fromDate, toDate, staffFilter]);

  // Fetch the caller's group memberships once. The /api/groups list is
  // already scoped server-side to the user's own groups (admins see all),
  // so its length is the true "how many agencies do I own" signal.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/groups");
        setOwnedGroups(Array.isArray(data) ? data : []);
      } catch {
        setOwnedGroups([]);
      }
    })();
  }, []);

  const exportCsv = () => {
    if (!view) return;
    const cell = (v) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "number") return String(v);
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const row = (arr) => arr.map(cell).join(",");

    const istNow = new Date().toLocaleString("en-IN", {
      ...IST_OPTS,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const filterName =
      effectiveAgencyFilter === "__all__"
        ? "All Agencies"
        : effectiveAgencyName || effectiveAgencyFilter;
    const t = view.totals;
    const billable = t.done + t.resent;
    const scope = isAdmin
      ? "Admin (all agencies)"
      : `Distributor — ${user?.display_name || user?.username}`;

    const lines = [];
    // Compact header (3 lines only)
    lines.push(row([`XYZ CHATTING — LPG DELIVERY LEDGER · ${filterName}`]));
    const periodText = days === "custom"
      ? `Period: ${periodLabel} (custom)`
      : days === "month"
      ? `Period: ${periodLabel} (this month)`
      : days === "yesterday"
      ? `Period: ${periodLabel}`
      : `Period: Last ${days} day(s)`;
    const staffText = staffFilter ? ` · Staff: ${staffFilter}` : "";
    lines.push(row([`${periodText}${staffText} · Generated ${istNow} IST · ${scope}`]));
    lines.push("");

    // ONE clean ledger table (matches the on-screen ledger)
    const showAgency = effectiveAgencyFilter === "__all__";
    const headerCols = showAgency
      ? ["Date", "Agency", "Sent", "Done", "Pending", "Billable", "Team Members"]
      : ["Date", "Sent", "Done", "Pending", "Billable", "Team Members"];
    lines.push(row(headerCols));
    const ledger = showAgency
      ? [...view.matrix].sort((a, b) =>
          a.date === b.date ? a.name.localeCompare(b.name) : a.date.localeCompare(b.date)
        )
      : [...view.by_date].sort((a, b) => a.date.localeCompare(b.date));
    ledger.forEach((r) => {
      const done = r.done + r.resent;
      const billable = done;
      const team = (r.uploaders || []).join(", ");
      if (showAgency) {
        lines.push(row([formatISTDate(r.date), r.name, r.total, done, r.wrong, billable, team]));
      } else {
        lines.push(row([formatISTDate(r.date), r.total, done, r.wrong, billable, team]));
      }
    });
    // TOTAL row
    const totalDone = t.done + t.resent;
    if (showAgency) {
      lines.push(row(["TOTAL", "", t.total, totalDone, t.wrong, totalDone, ""]));
    } else {
      lines.push(row(["TOTAL", t.total, totalDone, t.wrong, totalDone, ""]));
    }

    const csv = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    const tag =
      effectiveAgencyFilter === "__all__"
        ? "all"
        : filterName.replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    a.href = url;
    a.download = `XYZ_LPG_Ledger_${tag}_${days === "month" ? "thismonth" : days === "custom" ? "custom" : days === "yesterday" ? "yesterday" : `${days}d`}_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Clipboard copy for Google Sheets (navigator.clipboard with textarea fallback).
  const [copyState, setCopyState] = useState(""); // "" | "ok" | "fail"
  // Range-scan dedup modal — admin bulk cleanup across all agencies+dates.
  const [rangeDedup, setRangeDedup] = useState(null);
  // Single-day dedup modal (per-row 🧹 button)
  const [dedupModal, setDedupModal] = useState(null);
  const openRangeDedup = () => {
    // Default range: last 30 days (IST)
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 30);
    const iso = (d) => d.toISOString().slice(0, 10);
    setRangeDedup({
      from: iso(from),
      to: iso(today),
      dryResult: null,
      loading: false,
      executing: false,
    });
  };
  const runRangeDryScan = async () => {
    if (!rangeDedup) return;
    setRangeDedup((m) => ({ ...m, loading: true, dryResult: null }));
    try {
      const { data } = await api.post("/admin/wallet/dedup-scan-range", {
        date_from: rangeDedup.from,
        date_to: rangeDedup.to,
        dry_run: true,
      });
      setRangeDedup((m) => (m ? { ...m, dryResult: data, loading: false } : null));
    } catch (e) {
      toast.error(formatApiError(e));
      setRangeDedup((m) => (m ? { ...m, loading: false } : null));
    }
  };
  const confirmRangeDedup = async () => {
    if (!rangeDedup) return;
    setRangeDedup((m) => ({ ...m, executing: true }));
    try {
      const { data } = await api.post("/admin/wallet/dedup-scan-range", {
        date_from: rangeDedup.from,
        date_to: rangeDedup.to,
        dry_run: false,
      });
      toast.success(`Removed ${data.total_entries_removed} duplicate LPG entries across ${data.total_agencies_days_with_dups} bucket(s)`);
      setRangeDedup(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setRangeDedup((m) => (m ? { ...m, executing: false } : null));
    }
  };
  const openDedup = async (row) => {
    if (!row.group_id) {
      toast.error("Select a specific agency first (single-agency view)");
      return;
    }
    setDedupModal({
      group_id: row.group_id,
      date: row.date,
      agency_name: row.name || row.agency_name,
      dryResult: null,
      loading: true,
      executing: false,
    });
    try {
      const { data: dry } = await api.post("/admin/wallet/dedup-agency-day", {
        group_id: row.group_id,
        date: row.date,
        dry_run: true,
      });
      setDedupModal((m) => (m ? { ...m, dryResult: dry, loading: false } : null));
    } catch (e) {
      toast.error(formatApiError(e));
      setDedupModal(null);
    }
  };
  const confirmDedup = async () => {
    if (!dedupModal) return;
    setDedupModal((m) => ({ ...m, executing: true }));
    try {
      const { data: live } = await api.post("/admin/wallet/dedup-agency-day", {
        group_id: dedupModal.group_id,
        date: dedupModal.date,
        dry_run: false,
      });
      toast.success(`Removed ${live.entries_removed} duplicate LPG entries`);
      setDedupModal(null);
      // Reload the ledger to reflect the new counts
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setDedupModal((m) => (m ? { ...m, executing: false } : null));
    }
  };
  // Consumer Data Excel (DONE / billable consumers). `date` → single row day, else the report window.
  // "all" in the All-Agencies view → one separate Excel per agency that has DONE consumers.
  const [consumerBusy, setConsumerBusy] = useState("");
  const consumerPaused = user?.role === "member" && !user?.can_view_ledger;
  const saveConsumerFile = async (params) => {
    const resp = await api.get("/lpg/consumer-excel", { params, responseType: "blob" });
    const m = /filename="?([^";]+)"?/.exec(resp.headers?.["content-disposition"] || "");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(resp.data);
    a.download = m ? m[1] : "CONSUMERS.xlsx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  const downloadConsumers = async (key, { date, groupId } = {}) => {
    setConsumerBusy(key);
    try {
      const params = { ...(date ? { from_date: date, to_date: date } : rangeParams()), ...(staffFilter && { staff: staffFilter }) };
      const gid = groupId || (effectiveAgencyFilter !== "__all__" ? effectiveAgencyFilter : "");
      if (gid || key !== "all") {
        await saveConsumerFile({ ...params, ...(gid && { group_id: gid }) });
        return;
      }
      const per = new Map();
      (view?.matrix || []).forEach((r) => r.group_id && per.set(r.group_id, (per.get(r.group_id) || 0) + r.done + r.resent));
      const ids = [...per].filter(([, n]) => n > 0).map(([g]) => g);
      if (!ids.length) {
        toast.info("No DONE consumers in this period");
        return;
      }
      for (const g of ids) {
        await saveConsumerFile({ ...params, group_id: g });
        await new Promise((res) => setTimeout(res, 500));
      }
      toast.success(`${ids.length} agency file${ids.length > 1 ? "s" : ""} downloaded`);
    } catch (e) {
      const blob = e?.response?.data;
      let msg = formatApiError(e);
      if (blob instanceof Blob) {
        try { msg = JSON.parse(await blob.text()).detail || msg; } catch (_) { /* keep generic */ }
      }
      toast.error(msg);
    } finally {
      setConsumerBusy("");
    }
  };
  const writeClipboard = async (text) => {
    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  };
  // Copy "Agency<TAB>Sent" per agency (summed over the period) for Google Sheets.
  const copyAgencySent = async () => {
    if (!view) return;
    const sum = new Map();
    (view.matrix || []).forEach((r) => sum.set(r.name, (sum.get(r.name) || 0) + r.total));
    const lines = [...sum.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([n, v]) => `${n}\t${v}`);
    try {
      await writeClipboard(lines.join("\n"));
      setCopyState("ok");
    } catch (e) {
      setCopyState("fail");
    }
    setTimeout(() => setCopyState(""), 2200);
  };

  const isAdmin = user?.role === "admin";

  // When the caller owns exactly one agency (single-agency distributor),
  // collapse the "__all__" default to that agency so the title, Agency
  // column, and exports all read as that one agency instead of the
  // misleading "All Agencies". Admins / multi-agency owners are unaffected.
  //
  // We prefer `ownedGroups` (always reflects real membership) over
  // `data.by_group` (which is empty when the current days window has
  // zero deliveries) so the collapse holds even on empty days.
  const singleOwnedGroup = React.useMemo(() => {
    if (isAdmin) return null;
    if (ownedGroups.length === 1) return ownedGroups[0];
    return null;
  }, [isAdmin, ownedGroups]);

  const effectiveAgencyFilter = React.useMemo(() => {
    if (agencyFilter !== "__all__") return agencyFilter;
    if (singleOwnedGroup) return singleOwnedGroup.id;
    if (data && data.by_group.length === 1) return data.by_group[0].group_id;
    return "__all__";
  }, [agencyFilter, data, singleOwnedGroup]);

  // Resolve the agency name even when data.by_group is empty (no deliveries
  // in the selected window) — fall back to the owned-group name.
  const effectiveAgencyName = React.useMemo(() => {
    if (effectiveAgencyFilter === "__all__") return null;
    const fromData = data?.by_group.find(
      (g) => g.group_id === effectiveAgencyFilter
    )?.name;
    if (fromData) return fromData;
    if (singleOwnedGroup && singleOwnedGroup.id === effectiveAgencyFilter) {
      return singleOwnedGroup.name;
    }
    return "Agency";
  }, [effectiveAgencyFilter, data, singleOwnedGroup]);

  // Apply agency filter (client-side — backend already scoped by user role)
  const view = React.useMemo(() => {
    if (!data) return null;
    if (effectiveAgencyFilter === "__all__") return data;
    const filteredMatrix = data.matrix.filter((c) => c.group_id === effectiveAgencyFilter);
    const filteredByGroup = data.by_group.filter((g) => g.group_id === effectiveAgencyFilter);
    const filteredDetails = (data.details || []).filter(
      (d) => d.group_id === effectiveAgencyFilter
    );
    // Re-aggregate by_date from filtered matrix
    const byDateMap = {};
    const totals = { total: 0, done: 0, wrong: 0, resent: 0 };
    for (const c of filteredMatrix) {
      const b = byDateMap[c.date] || {
        date: c.date,
        total: 0,
        done: 0,
        wrong: 0,
        resent: 0,
        uploaders: [],
      };
      b.total += c.total;
      b.done += c.done;
      b.wrong += c.wrong;
      b.resent += c.resent;
      // Merge uploader lists (distinct, sorted) from every matrix cell that
      // falls under this date so filtered-view rows still surface staff names.
      const merged = new Set([...(b.uploaders || []), ...(c.uploaders || [])]);
      b.uploaders = Array.from(merged).sort();
      byDateMap[c.date] = b;
      totals.total += c.total;
      totals.done += c.done;
      totals.wrong += c.wrong;
      totals.resent += c.resent;
    }
    return {
      ...data,
      totals,
      by_date: Object.values(byDateMap).sort((a, b) =>
        a.date < b.date ? 1 : -1
      ),
      by_group: filteredByGroup,
      matrix: filteredMatrix,
      details: filteredDetails,
    };
  }, [data, effectiveAgencyFilter]);

  // Build the ledger rows: when an agency is selected, group by date only;
  // when "all agencies" is on, keep the matrix as-is (date × agency).
  const ledgerRows = React.useMemo(() => {
    if (!view) return [];
    if (effectiveAgencyFilter === "__all__") {
      return [...view.matrix].sort((a, b) =>
        a.date === b.date ? a.name.localeCompare(b.name) : a.date < b.date ? 1 : -1
      );
    }
    // Single-agency view: `by_date` rows lack group_id, but we already
    // know it from the filter. Inject it + the resolved agency name so
    // per-row admin actions (delete, dedup) work identically.
    return [...view.by_date].map((d) => ({
      ...d,
      group_id: effectiveAgencyFilter,
      name: effectiveAgencyName || "",
    }));
  }, [view, effectiveAgencyFilter, effectiveAgencyName]);

  // "01 Jun 2026 → 07 Jun 2026" — anchored to the user-selected days window.
  const periodLabel = React.useMemo(() => {
    const fmt = (d) =>
      d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        ...IST_OPTS,
      });
    if (days === "custom") {
      // Custom range — prefer the exact YYYY-MM-DD inputs; fallback to
      // whatever the backend returned so the label is never blank.
      const f = fromDate || data?.from_date || "";
      const t = toDate || data?.to_date || "";
      if (f && t) {
        return `${fmt(new Date(f + "T00:00:00"))} → ${fmt(new Date(t + "T00:00:00"))}`;
      }
      if (f) return `From ${fmt(new Date(f + "T00:00:00"))}`;
      if (t) return `Till ${fmt(new Date(t + "T00:00:00"))}`;
      return "Custom range";
    }
    if (days === "yesterday") {
      return `${fmt(new Date(istYesterday() + "T00:00:00"))} (yesterday)`;
    }
    if (days === 1) {
      // "Today" uses an IST calendar-day snapshot — match the label.
      const istToday = new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
      return `${fmt(new Date(istToday + "T00:00:00"))} (today)`;
    }
    if (days === "month") {
      // "This month" — 1st of current IST month → today.
      const istToday = new Date().toLocaleDateString("en-CA", {
        timeZone: "Asia/Kolkata",
      });
      const monthStart = istToday.slice(0, 8) + "01";
      return `${fmt(new Date(monthStart + "T00:00:00"))} → ${fmt(new Date(istToday + "T00:00:00"))}`;
    }
    const today = new Date();
    const end = today;
    const start = new Date(today);
    start.setDate(today.getDate() - (days - 1));
    return `${fmt(start)} → ${fmt(end)}`;
  }, [days, fromDate, toDate, data]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#F5F5F0] via-[#F0F1EC] to-[#EBE8E0]"
      style={{ fontFamily: "Work Sans, sans-serif" }}
    >
      {/* Top bar */}
      <header className="bg-gradient-to-r from-[#385C4B] to-[#2D4A3C] text-[#F5F5F0] px-4 py-3 flex items-center gap-3 shadow-md">
        <button
          onClick={() => navigate("/home")}
          data-testid="lpg-report-back"
          className="p-2 rounded-md hover:bg-white/10 transition-colors"
          title="Home"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="flex items-center gap-2 flex-1">
          <Flame className="w-5 h-5" strokeWidth={1.5} />
          <div>
            <div
              className="font-semibold text-base leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              LPG Delivery Report
            </div>
            <div className="text-[10px] uppercase tracking-wider opacity-70 font-mono">
              {isAdmin ? "Admin · All Agencies" : `Distributor · ${user?.display_name || user?.username}`}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            logout();
            navigate("/login", { replace: true });
          }}
          className="p-2 rounded-md hover:bg-white/10 transition-colors"
          title="Logout"
        >
          <LogOut className="w-4 h-4" strokeWidth={2} />
        </button>
      </header>

      <main className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
        {/* Controls */}
        <div className="bg-white border border-[#DCDCD3] rounded-lg p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm">
          <div className="text-xs text-[#6B6B61] font-mono">
            All times in IST · {view?.totals?.total ?? 0} codes in view
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {data && data.by_group.length > 1 && (
              <select
                value={agencyFilter}
                onChange={(e) => setAgencyFilter(e.target.value)}
                data-testid="lpg-report-agency-filter"
                className="px-3 py-1.5 border border-[#DCDCD3] rounded-md text-sm bg-white max-w-[180px]"
              >
                <option value="__all__">All Agencies</option>
                {data.by_group.map((g) => (
                  <option key={g.group_id} value={g.group_id}>
                    {g.name}
                  </option>
                ))}
              </select>
            )}
            <select
              value={days}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom" || v === "month" || v === "yesterday") {
                  setDays(v);
                } else {
                  setDays(parseInt(v, 10));
                }
              }}
              data-testid="lpg-report-range"
              className="px-3 py-1.5 border border-[#DCDCD3] rounded-md text-sm bg-white"
            >
              <option value={1}>Today</option>
              <option value="yesterday">Yesterday</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value="month">This month</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 1 year</option>
              <option value="custom">Custom range…</option>
            </select>
            {days === "custom" && (
              <>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  data-testid="lpg-report-from-date"
                  className="px-2 py-1.5 border border-[#DCDCD3] rounded-md text-sm bg-white"
                  title="From (IST)"
                />
                <span className="text-xs text-[#6B6B61] font-mono">→</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  data-testid="lpg-report-to-date"
                  className="px-2 py-1.5 border border-[#DCDCD3] rounded-md text-sm bg-white"
                  title="To (IST)"
                />
              </>
            )}
            {/* Team member filter — populated from the currently-loaded
                report's staff_options so the dropdown always reflects who
                actually uploaded in this window. */}
            {(data?.staff_options?.length || 0) > 0 && (
              <select
                value={staffFilter}
                onChange={(e) => setStaffFilter(e.target.value)}
                data-testid="lpg-report-staff-filter"
                className="px-3 py-1.5 border border-[#DCDCD3] rounded-md text-sm bg-white max-w-[180px]"
                title="Filter by team member (uploader)"
              >
                <option value="">All Team Members</option>
                {data.staff_options.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
            <button
              onClick={load}
              className="px-3 py-1.5 text-xs border border-[#385C4B]/40 text-[#385C4B] rounded-md hover:bg-[#385C4B]/5 transition-colors flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" strokeWidth={2} /> Refresh
            </button>
            {/* Feb 2026 admin toggle: reveal per-row edit (pencil) buttons
                across all dates. Hidden by default so casual viewing
                doesn't expose the reassign flow. */}
            {isAdmin && (
              <button
                onClick={() => setShowReassign((v) => !v)}
                data-testid="lpg-report-toggle-reassign"
                title={showReassign
                  ? "Hide per-row edit buttons (safe viewing mode)"
                  : "Show per-row edit buttons on every date — reassign team member / agency"}
                className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1.5 border ${
                  showReassign
                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
                    : "border-blue-500/40 text-blue-700 hover:bg-blue-500/5"
                }`}
              >
                <Pencil className="w-3 h-3" strokeWidth={2} />
                {showReassign ? "Edit ON" : "Show Edit"}
              </button>
            )}
            {user?.role === "admin" && days === 1 && (
              <>
                <select
                  value={deleteTodayOp}
                  onChange={(e) => setDeleteTodayOp(e.target.value)}
                  data-testid="lpg-report-delete-today-op"
                  className="px-2 py-1.5 text-xs border border-red-300 text-red-700 rounded-md bg-white focus:outline-none focus:border-red-500"
                  title="Kaunsa operator delete karna hai"
                >
                  <option value="all">All operators</option>
                  <option value="indane">Indane (IOCL)</option>
                  <option value="bharat">Bharat (BPCL)</option>
                  <option value="hpcl">HPCL</option>
                </select>
                <button
                  onClick={deleteTodayEntries}
                  disabled={deletingToday}
                  data-testid="lpg-report-delete-today-btn"
                  title="SIRF aaj (IST today) ki entries PERMANENTLY delete karo — filter/date range chahe kuch bhi ho, purane din safe rahenge"
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                >
                  {deletingToday ? (
                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
                  ) : (
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                  )}
                  Delete Today
                </button>
                <button
                  onClick={fixTodayLedger}
                  disabled={fixingLedger}
                  data-testid="lpg-report-fix-ledger-btn"
                  title="Aaj ki entries me status null ho to 'correct' set kar do — Staff/Agency Ledger populate hoga (safe, idempotent)"
                  className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                >
                  {fixingLedger ? (
                    <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
                  ) : (
                    <RefreshCw className="w-3 h-3" strokeWidth={2} />
                  )}
                  Fix Ledger
                </button>
              </>
            )}
            <button
              onClick={() => downloadConsumers("all")}
              disabled={!view || consumerPaused || consumerBusy === "all"}
              data-testid="lpg-report-consumers"
              title={consumerPaused ? "Download paused — no activity in last 30 days" : "Download DONE consumers — one Excel per agency"}
              className="px-3 py-1.5 text-xs bg-white text-[#385C4B] border border-[#385C4B] rounded-md hover:bg-[#385C4B]/5 transition-colors disabled:opacity-40 flex items-center gap-1.5"
            >
              {consumerBusy === "all" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3" strokeWidth={2} />}
              {effectiveAgencyFilter === "__all__" ? "All Consumers" : "Consumer Data"}
            </button>
            <button
              onClick={exportCsv}
              disabled={!view}
              data-testid="lpg-report-export"
              className="px-3 py-1.5 text-xs bg-[#385C4B] text-white rounded-md hover:bg-[#2D4A3C] transition-colors disabled:opacity-40 flex items-center gap-1.5"
            >
              <Download className="w-3 h-3" strokeWidth={2} /> Excel
            </button>
            <button
              onClick={() => window.print()}
              disabled={!view}
              data-testid="lpg-report-pdf"
              className="px-3 py-1.5 text-xs bg-[#7c3aed] text-white rounded-md hover:bg-[#6d28d9] transition-colors disabled:opacity-40 flex items-center gap-1.5"
            >
              <Printer className="w-3 h-3" strokeWidth={2} /> PDF
            </button>
            {isAdmin && (
              <button
                onClick={openRangeDedup}
                data-testid="lpg-report-scan-range"
                title="Scan a date range across all agencies and remove duplicate LPG entries in one click"
                className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center gap-1.5"
              >
                🧹 Scan Range
              </button>
            )}
          </div>
        </div>

        {/* Undo Uploads panel removed Feb 2026 — replaced by permanent
            "Delete Today" (bulk) + per-row × delete on the ledger table
            below. Batch soft-delete was confusing users. */}

        {/* Delivered-Excel batch history + undo (Sep 2026). Reverts BOTH
            new green cards AND flip-patched pending entries in one
            click — the ONLY safe way to fully rollback a bad delivered
            import. */}
        {isAdmin && (
          <DeliveredBatchesPanel
            onUndone={() => {
              // Refresh the current LPG report window so counts + the
              // ledger table reflect the reverted state.
              try {
                fetch(document.location.href).catch(() => {});
              } catch {
                /* no-op */
              }
            }}
          />
        )}

        {err && (
          <div className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
            {err}
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin text-[#385C4B]" />
          </div>
        )}

        {!loading && view && (
          <div className="space-y-5 print-area">
            {/* Grand Total banner — ALWAYS shows totals across ALL agencies,
                regardless of the current agency filter. Gives admins /
                distributors an at-a-glance executive view even while
                drilling into a single agency below. */}
            {data && isAdmin && data.by_group && data.by_group.length > 1 && (
              <div
                className="bg-gradient-to-r from-[#385C4B] to-[#2D4A3C] text-white rounded-lg p-4 shadow-md"
                data-testid="lpg-report-grand-total-banner"
              >
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 font-mono">
                      Grand Total · All Agencies · {periodLabel}
                    </div>
                    <div
                      className="text-lg font-semibold mt-0.5"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {data.by_group.length} agencies aggregated
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 sm:gap-6 text-right">
                    <div data-testid="lpg-grand-total-sent">
                      <div className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Sent</div>
                      <div className="text-2xl font-mono font-semibold">{data.totals.total}</div>
                    </div>
                    <div data-testid="lpg-grand-total-done">
                      <div className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Done</div>
                      <div className="text-2xl font-mono font-semibold text-emerald-300">
                        {data.totals.done + data.totals.resent}
                      </div>
                    </div>
                    <div data-testid="lpg-grand-total-pending">
                      <div className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Pending</div>
                      <div className="text-2xl font-mono font-semibold text-rose-300">
                        {data.totals.wrong}
                      </div>
                    </div>
                    <div data-testid="lpg-grand-total-billable">
                      <div className="text-[10px] uppercase tracking-wider opacity-70 font-mono">Billable</div>
                      <div className="text-2xl font-mono font-semibold text-violet-300">
                        {data.totals.done + data.totals.resent}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Header card — Agency + Period (visible & in PDF) */}
            <div
              className="bg-white border border-[#DCDCD3] rounded-lg p-5 shadow-sm"
              data-testid="lpg-report-header-card"
            >
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div
                    className="text-[10px] uppercase tracking-[0.2em] text-[#6B6B61] font-mono"
                  >
                    XYZ Chatting · LPG Delivery Ledger
                  </div>
                  <div
                    className="text-2xl font-semibold text-[#1C1C19] mt-1"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {effectiveAgencyFilter === "__all__"
                      ? "All Agencies"
                      : effectiveAgencyName || "Agency"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-mono">
                    Period (IST)
                  </div>
                  <div
                    className="text-sm font-mono text-[#1C1C19] mt-1"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    {periodLabel}
                  </div>
                </div>
              </div>
            </div>

            {/* Compact summary strip */}
            <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden">
              <div className="grid grid-cols-4">
                {[
                  { label: "Sent", value: view.totals.total, color: "#385C4B" },
                  { label: "Done", value: view.totals.done + view.totals.resent, color: "#10b981" },
                  { label: "Pending", value: view.totals.wrong, color: "#ef4444" },
                  {
                    label: "Billable",
                    value: view.totals.done + view.totals.resent,
                    color: "#7c3aed",
                  },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className={`px-4 py-3 ${i < 3 ? "border-r border-[#DCDCD3]" : ""}`}
                    data-testid={`lpg-summary-${s.label.toLowerCase()}`}
                  >
                    <div
                      className="text-[10px] uppercase tracking-wider font-mono"
                      style={{ color: s.color }}
                    >
                      {s.label}
                    </div>
                    <div
                      className="text-2xl font-semibold mt-0.5"
                      style={{ color: s.color, fontFamily: "Inter, sans-serif" }}
                    >
                      {s.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* THE Ledger — single clean table */}
            <div className="bg-white border border-[#DCDCD3] rounded-lg overflow-hidden">
              <div className="overflow-x-auto" data-testid="lpg-ledger">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      {effectiveAgencyFilter === "__all__" && (
                        <th className="text-left px-3 py-3">Agency</th>
                      )}
                      <th className="text-right px-3 py-3">
                        <span className="inline-flex items-center gap-1.5">
                          Sent
                          <button
                            onClick={() => downloadConsumers("all")}
                            disabled={!view || consumerPaused || consumerBusy === "all"}
                            data-testid="lpg-report-consumers-all-head"
                            title={consumerPaused ? "Download paused — no activity in last 30 days" : "Download a separate Excel for every agency with DONE consumers"}
                            className="print:hidden p-1 rounded text-[#385C4B] hover:bg-[#385C4B]/10 transition-colors disabled:opacity-40"
                          >
                            {consumerBusy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={2} />}
                          </button>
                        </span>
                      </th>
                      <th className="text-right px-3 py-3 text-emerald-700">Done</th>
                      <th className="text-right px-3 py-3 text-red-600">Pending</th>
                      <th className="text-right px-3 py-3 text-[#7c3aed]">
                        <span className="inline-flex items-center gap-1.5">
                          Billable
                          <button
                            onClick={copyAgencySent}
                            disabled={!view}
                            data-testid="lpg-report-copy-agency"
                            title={copyState === "ok" ? "Copied!" : "Copy Agency + Sent for Google Sheets"}
                            className={`print:hidden p-1 rounded transition-colors disabled:opacity-40 ${
                              copyState === "ok"
                                ? "bg-[#385C4B] text-white"
                                : copyState === "fail"
                                ? "bg-red-50 text-red-700"
                                : "text-[#385C4B] hover:bg-[#385C4B]/10"
                            }`}
                          >
                            {copyState === "ok" ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2} />}
                          </button>
                        </span>
                      </th>
                      <th className="text-left px-3 py-3">Team Members</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={effectiveAgencyFilter === "__all__" ? 7 : 6}
                          className="text-center py-8 text-[#6B6B61]"
                        >
                          No deliveries in this period.
                        </td>
                      </tr>
                    )}
                    {ledgerRows.map((r, i) => (
                      <tr
                        key={`${r.date}-${r.group_id || "agg"}-${i}`}
                        className="border-t border-[#F0F0EA]"
                      >
                        <td
                          className="px-4 py-2.5 font-mono text-xs whitespace-nowrap"
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          {formatISTDate(r.date)}
                        </td>
                        {effectiveAgencyFilter === "__all__" && (
                          <td
                            className="px-3 py-2.5 truncate max-w-[220px]"
                            title={r.name}
                            style={{ fontFamily: "Outfit, sans-serif" }}
                          >
                            {r.name}
                          </td>
                        )}
                        <td className="px-3 py-2.5 text-right font-mono">
                          <span className="inline-flex items-center gap-1.5 justify-end">
                            {r.total}
                            {r.group_id && r.done + r.resent > 0 && (
                              <button
                                onClick={() => downloadConsumers(`row-${i}`, { date: r.date, groupId: r.group_id })}
                                disabled={consumerPaused || consumerBusy === `row-${i}`}
                                title={consumerPaused ? "Download paused — no activity in last 30 days" : `Download ${r.done + r.resent} consumers of this day`}
                                data-testid={`lpg-consumers-row-${i}`}
                                className="print:hidden text-[#385C4B] hover:text-white hover:bg-[#385C4B] rounded p-1 transition disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#385C4B]"
                              >
                                {consumerBusy === `row-${i}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                              </button>
                            )}
                            {isAdmin && r.group_id && r.total > 0 && (
                              <button
                                onClick={() => openDedup(r)}
                                title="Clean duplicate LPG entries for this row (admin-only)"
                                data-testid={`lpg-dedup-btn-${i}`}
                                className="text-[#8A6046] hover:text-red-600 hover:bg-red-500/10 rounded px-1 text-[10px] transition"
                              >
                                🧹
                              </button>
                            )}
                            {isAdmin && r.group_id && r.total > 0 && showReassign && (
                              <button
                                onClick={() => openReassign(r)}
                                title="Reassign entries: change team member / agency (turn OFF Show Edit to hide these)"
                                data-testid={`lpg-reassign-btn-${i}`}
                                className="text-blue-600 hover:text-white hover:bg-blue-600 rounded p-1 transition"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                            {isAdmin && r.group_id && r.total > 0 && (
                              <button
                                onClick={() => deleteSingleRow(r)}
                                disabled={deletingRow === `${r.date}::${r.group_id}`}
                                title="Is date + agency ki SAARI entries PERMANENTLY delete karo"
                                data-testid={`lpg-delete-row-${i}`}
                                className="text-red-500 hover:text-white hover:bg-red-600 rounded p-1 transition disabled:opacity-40"
                              >
                                {deletingRow === `${r.date}::${r.group_id}` ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-emerald-700 font-semibold">
                          {r.done + r.resent}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono text-red-600">
                          {r.wrong}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-[#7c3aed]">
                          {r.done + r.resent}
                        </td>
                        <td
                          className="px-3 py-2.5 text-xs text-[#1C1C19] max-w-[240px] truncate"
                          title={(r.uploaders || []).join(", ")}
                          data-testid={`lpg-ledger-team-${i}`}
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          {(r.uploaders || []).join(", ") || (
                            <span className="text-[#A7A79D]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {ledgerRows.length > 0 && (
                    <tfoot
                      className="bg-[#F5F5F0] border-t-2 border-[#385C4B] text-[#1C1C19]"
                      data-testid="lpg-ledger-total"
                    >
                      <tr>
                        <td
                          className="px-4 py-3 font-bold text-xs uppercase tracking-wider"
                          colSpan={effectiveAgencyFilter === "__all__" ? 2 : 1}
                          style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                          TOTAL
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold">
                          {view.totals.total}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold text-emerald-700">
                          {view.totals.done + view.totals.resent}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold text-red-600">
                          {view.totals.wrong}
                        </td>
                        <td className="px-3 py-3 text-right font-mono font-bold text-[#7c3aed]">
                          {view.totals.done + view.totals.resent}
                        </td>
                        <td className="px-3 py-3" />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>

            <div className="text-[11px] text-[#A7A79D] italic">
              Done = first-try verified + resent ✓. Pending = currently awaiting resend. Billable = Done.
            </div>
          </div>
        )}
      </main>
      {rangeDedup && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="lpg-range-dedup-modal"
        >
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-[#DCDCD3] flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1C1C19]">
                  🧹 Scan Range — Remove All Duplicate LPG Entries
                </div>
                <div className="text-xs text-[#6B6B61]">
                  Bulk cleanup across all agencies. Wrong-status entries always preserved.
                </div>
              </div>
              <button
                onClick={() => setRangeDedup(null)}
                className="text-[#6B6B61] hover:text-[#1C1C19]"
                data-testid="lpg-range-dedup-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="flex items-end gap-3 flex-wrap">
                <label className="flex flex-col gap-1 text-xs text-[#6B6B61]">
                  From (IST)
                  <input
                    type="date"
                    value={rangeDedup.from}
                    onChange={(e) => setRangeDedup((m) => ({ ...m, from: e.target.value, dryResult: null }))}
                    data-testid="lpg-range-dedup-from"
                    className="px-2 py-1.5 border border-[#DCDCD3] rounded text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-[#6B6B61]">
                  To (IST)
                  <input
                    type="date"
                    value={rangeDedup.to}
                    onChange={(e) => setRangeDedup((m) => ({ ...m, to: e.target.value, dryResult: null }))}
                    data-testid="lpg-range-dedup-to"
                    className="px-2 py-1.5 border border-[#DCDCD3] rounded text-sm"
                  />
                </label>
                <button
                  onClick={runRangeDryScan}
                  disabled={rangeDedup.loading}
                  data-testid="lpg-range-dedup-scan"
                  className="px-3 py-1.5 text-sm border border-[#385C4B] text-[#385C4B] rounded hover:bg-[#385C4B]/10 disabled:opacity-50 flex items-center gap-2"
                >
                  {rangeDedup.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Scan
                </button>
              </div>

              {rangeDedup.dryResult && (
                rangeDedup.dryResult.total_entries_removed === 0 ? (
                  <div className="text-sm text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                    <b>No duplicates found</b> across {rangeDedup.dryResult.total_agencies_days_scanned} agency/day bucket(s) in this range. Data is already clean.
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-amber-900 bg-amber-500/10 border border-amber-500/30 rounded p-3">
                      <b>{rangeDedup.dryResult.total_entries_removed}</b> duplicate LPG entries found across{" "}
                      <b>{rangeDedup.dryResult.total_agencies_days_with_dups}</b> agency-day bucket(s).
                      Clicking <b>Remove All</b> will keep the earliest occurrence of each LPG ID and strip the rest.
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">
                      Buckets ({rangeDedup.dryResult.per_bucket.length}):
                    </div>
                    <div className="max-h-72 overflow-y-auto text-xs border border-[#DCDCD3] rounded">
                      <table className="w-full">
                        <thead className="bg-[#F5F5F0] sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-mono text-[10px] uppercase tracking-wider text-[#6B6B61]">Date</th>
                            <th className="px-2 py-1.5 text-left font-mono text-[10px] uppercase tracking-wider text-[#6B6B61]">Agency</th>
                            <th className="px-2 py-1.5 text-right font-mono text-[10px] uppercase tracking-wider text-[#6B6B61]">Dup LPGs</th>
                            <th className="px-2 py-1.5 text-right font-mono text-[10px] uppercase tracking-wider text-[#6B6B61]">Entries</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rangeDedup.dryResult.per_bucket.map((b, i) => (
                            <tr key={`${b.date}-${b.group_name || i}`} className="border-t border-[#F0F0EA]">
                              <td className="px-2 py-1 font-mono text-[11px]">{b.date}</td>
                              <td className="px-2 py-1">{b.group_name}</td>
                              <td className="px-2 py-1 text-right font-mono text-[#385C4B]">{b.duplicate_lpgs_count}</td>
                              <td className="px-2 py-1 text-right font-mono text-red-700 font-semibold">{b.entries_removed}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2">
              <button
                onClick={() => setRangeDedup(null)}
                data-testid="lpg-range-dedup-cancel"
                className="px-3 py-1.5 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
              >
                Cancel
              </button>
              {rangeDedup.dryResult && rangeDedup.dryResult.total_entries_removed > 0 && (
                <button
                  onClick={confirmRangeDedup}
                  disabled={rangeDedup.executing}
                  data-testid="lpg-range-dedup-confirm"
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {rangeDedup.executing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remove All {rangeDedup.dryResult.total_entries_removed} Duplicates
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {reassignModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
          onClick={(e) => {
            if (
              e.target === e.currentTarget &&
              !reassignModal.submitting
            ) {
              setReassignModal(null);
            }
          }}
          data-testid="lpg-reassign-modal"
        >
          <div
            className="bg-white w-full max-w-md rounded-lg shadow-2xl border border-[#DCDCD3]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-[#DCDCD3] flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#1C1C19]">
                <Pencil className="w-4 h-4 text-blue-600" />
                Reassign entries · {formatISTDate(reassignModal.row.date)}
              </div>
              <button
                onClick={() => !reassignModal.submitting && setReassignModal(null)}
                className="p-1 hover:bg-[#EDEDE5] rounded"
                data-testid="lpg-reassign-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3 text-sm">
              <div className="bg-[#F5F5EE] border border-[#DCDCD3] rounded p-2.5 text-xs font-mono">
                <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] mb-1">Source</div>
                <div>
                  <b>{reassignModal.row.name || "—"}</b>
                </div>
                <div className="text-[#385C4B]">
                  {(reassignModal.row.uploaders || ["—"]).join(", ")} · {reassignModal.row.total} entries
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-semibold">
                  Move to Team Member
                </label>
                <select
                  value={reassignModal.to_uploader}
                  onChange={(e) =>
                    setReassignModal((m) => ({ ...m, to_uploader: e.target.value }))
                  }
                  className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm bg-white"
                  data-testid="lpg-reassign-uploader"
                >
                  <option value="">— Select team member —</option>
                  {(data?.staff_options || []).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-semibold">
                  Agency (optional — leave same to keep)
                </label>
                <select
                  value={reassignModal.to_group_id}
                  onChange={(e) =>
                    setReassignModal((m) => ({ ...m, to_group_id: e.target.value }))
                  }
                  className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm bg-white"
                  data-testid="lpg-reassign-agency"
                >
                  {(ownedGroups || []).map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B6B61] font-semibold">
                  Quantity <span className="text-red-600">*</span> · max {reassignModal.row.total}
                </label>
                <input
                  type="number"
                  min="1"
                  max={reassignModal.row.total}
                  required
                  value={reassignModal.quantity}
                  onChange={(e) => {
                    // Clamp on the fly — don't let the input hold a number
                    // above the row total. Empty string is fine (validation
                    // will kick in on submit).
                    let v = e.target.value;
                    if (v !== "") {
                      const n = parseInt(v, 10);
                      if (!isNaN(n) && n > reassignModal.row.total) {
                        v = String(reassignModal.row.total);
                      }
                    }
                    setReassignModal((m) => ({ ...m, quantity: v }));
                  }}
                  placeholder={`1 – ${reassignModal.row.total}`}
                  className="w-full mt-1 px-3 py-2 border border-[#DCDCD3] rounded text-sm font-mono focus:outline-none focus:border-blue-500"
                  data-testid="lpg-reassign-quantity"
                />
                <div className="text-[10px] text-[#6B6B61] mt-1">
                  Newest-first entries move honge (agar aadhe entries move
                  karna hai to source message split hoke naya message
                  banega target uploader ke naam pe).
                  <b> Max <b>{reassignModal.row.total}</b> — es se jyada
                  possible nahi.</b>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2">
              <button
                onClick={() => !reassignModal.submitting && setReassignModal(null)}
                className="px-3 py-1.5 text-xs border border-[#DCDCD3] rounded hover:bg-[#F5F5EE]"
                data-testid="lpg-reassign-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitReassign}
                disabled={
                  reassignModal.submitting ||
                  !reassignModal.quantity ||
                  !reassignModal.to_uploader ||
                  parseInt(reassignModal.quantity, 10) < 1 ||
                  parseInt(reassignModal.quantity, 10) > reassignModal.row.total
                }
                className="px-4 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                data-testid="lpg-reassign-submit"
              >
                {reassignModal.submitting ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Moving…</>
                ) : (
                  <><Pencil className="w-3 h-3" /> Reassign</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {dedupModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="lpg-dedup-modal"
        >
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-[#DCDCD3] flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-[#1C1C19]">
                  🧹 Clean Duplicate LPG Entries
                </div>
                <div className="text-xs text-[#6B6B61]">
                  {dedupModal.agency_name} · {formatISTDate(dedupModal.date)}
                </div>
              </div>
              <button
                onClick={() => setDedupModal(null)}
                className="text-[#6B6B61] hover:text-[#1C1C19]"
                data-testid="lpg-dedup-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {dedupModal.loading ? (
                <div className="flex items-center gap-2 text-[#6B6B61]">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Scanning for duplicates…
                </div>
              ) : !dedupModal.dryResult ? null : dedupModal.dryResult.entries_removed === 0 ? (
                <div className="text-sm text-emerald-800 bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <b>No duplicates found</b> — {dedupModal.agency_name}&#39;s {formatISTDate(dedupModal.date)} data is already clean.
                </div>
              ) : (
                <>
                  <div className="text-sm text-amber-900 bg-amber-500/10 border border-amber-500/30 rounded p-3">
                    <b>{dedupModal.dryResult.entries_removed}</b> duplicate LPG entries detected across{" "}
                    <b>{dedupModal.dryResult.total_messages_scanned}</b> messages on this day.
                    Clicking <b>Remove</b> will keep the earliest occurrence of each LPG ID and strip the rest.
                    Complaint entries (status=&quot;wrong&quot;) are always preserved.
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#6B6B61]">
                    Duplicates ({dedupModal.dryResult.duplicate_lpgs.length}):
                  </div>
                  <div className="max-h-72 overflow-y-auto text-xs font-mono border border-[#DCDCD3] rounded">
                    {dedupModal.dryResult.duplicate_lpgs.slice(0, 200).map((d, i) => (
                      <div key={`${d.lpg_id}-${d.date || i}`} className="px-2 py-1 border-b border-[#F0F0EA] last:border-b-0 flex flex-wrap gap-2">
                        <span className="text-[#385C4B] font-semibold">{d.lpg_id}</span>
                        <span className="text-[#6B6B61]">
                          keep in msg {d.kept_msg_id.slice(0, 8)}… ·
                          remove from {d.removed_from_msg_ids.length} other msg
                        </span>
                      </div>
                    ))}
                    {dedupModal.dryResult.duplicate_lpgs.length > 200 && (
                      <div className="px-2 py-1 text-[#6B6B61]">
                        … {dedupModal.dryResult.duplicate_lpgs.length - 200} more
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="px-5 py-3 border-t border-[#DCDCD3] flex items-center justify-end gap-2">
              <button
                onClick={() => setDedupModal(null)}
                data-testid="lpg-dedup-cancel"
                className="px-3 py-1.5 text-sm border border-[#DCDCD3] rounded hover:bg-[#F5F5F0]"
              >
                Cancel
              </button>
              {dedupModal.dryResult && dedupModal.dryResult.entries_removed > 0 && (
                <button
                  onClick={confirmDedup}
                  disabled={dedupModal.executing}
                  data-testid="lpg-dedup-confirm"
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {dedupModal.executing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remove {dedupModal.dryResult.entries_removed} duplicate{dedupModal.dryResult.entries_removed !== 1 ? "s" : ""}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
